import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  AutomationConditionItem,
  AutomationConditionModel,
  SelectedCondition
} from './automation-condition.model';
import { FilterOperatorType } from '../odata-filter/models/filter-operator-type';
import { FilterOperator, OPERATORS } from '../odata-filter/models/operators';
import { getTextForEvidence } from '../../models/credential/evidenceDefinition.model';
import { first, map } from 'rxjs/operators';
import { AttributeService } from '../../services/attribute.service';
import { PersonDetailsService } from '../../services/person-details.service';
import { EvidenceAutomationConditionItem } from 'src/app/admin/admin-home/admin-credentials/admin-credential-scenario/admin-credential-scenario-details/admin-requirement-side-bar/evidence-automation-condition-item';
import { SearchValueType } from '../odata-filter/models/search-value-type';
import { Certification } from 'src/app/people/models/certification';
import { AvailableCondition } from '../../models/automation/AvailableCondition';
import { createSelectedCondition } from './selected-condition.factory';

@Component({
  selector: 'app-automation-condition-builder',
  templateUrl: './automation-condition-builder.component.html',
  styleUrls: ['./automation-condition-builder.component.scss'],
  standalone: false
})
export class AutomationConditionBuilderComponent implements OnChanges {
  @Output() updateIsDisabled = new EventEmitter<boolean>();

  /** will convert these rawAvailableConditions into availableConditions after a bit of massaging  */
  @Input()
  public rawAvailableConditions: AvailableCondition[];
  /** happy path for setting existing conditions. Gets massaged with availableConditions to become existingConditions */
  @Input()
  public rawExistingConditions: AutomationConditionModel[];
  /** existing conditions come from massaging rawExistingConditions and availableConditions
   * Can also be set manually if condition is "empty". For instance you want to hide the 'add' button, but want an initial empty condition */
  @Input()
  public existingConditions: SelectedCondition[];

  @Input()
  hideAddRemove: boolean;

  /**
   * Connector word shown before each condition after the first. Defaults to 'And' (the AND
   * semantics automations use). The aggregate filter passes 'Or' when its conditions combine with
   * OR, so the per-row label matches the chosen combinator.
   */
  @Input()
  conjunction: 'And' | 'Or' = 'And';

  /** availableConditions come from massaging rawAvailableConditions */
  public availableConditions: EvidenceAutomationConditionItem[];

  constructor(
    private attributeService: AttributeService,
    private personDetailsService: PersonDetailsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.rawAvailableConditions?.currentValue) {
      return;
    }
    if (this.availableConditions) {
      console.error('expected this.availableEnhancedConditions to be falsy');
    }
    this._conditionReadyCount = 0;
    if (this.rawExistingConditions) {
      this.existingConditions = [];
    }

    this.availableConditions = this.rawAvailableConditions
      .map(this.convertEvidenceDefinitionToEvidenceAutomationConditionItem)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    this.checkIfAvailableConditionsReady();
  }

  private _cachedCertifications: Certification[];
  private _conditionReadyCount = 0;
  private convertEvidenceDefinitionToEvidenceAutomationConditionItem = (
    evidence: AvailableCondition
  ) => {
    const condition = new EvidenceAutomationConditionItem(
      evidence.id,
      evidence.evidenceDefinitionName
        ? evidence.evidenceDefinitionName
        : getTextForEvidence(evidence.evidenceType),
      this.conditionReady //callBack for when initialization is finished
    );

    //depending on evidenceType we need to add more meta data to EvidenceAutomationConditionItem
    if (evidence.evidenceType == 'CustomField' || evidence.evidenceType == 'Skill') {
      this.attributeService.attributes
        .pipe(first()) //don't need to unsubscribe because with 'first()' is unnecessary
        .pipe(map((attributes) => attributes.find((x) => x.id == evidence.attributeDefinitionId)))
        .subscribe((attribute) => {
          condition.setAttributeValues(attribute);
        });
    } else if (evidence.evidenceType === 'EvaluationRating') {
      condition.setValueType(SearchValueType.Number);
    } else if (evidence.evidenceType == 'ActiveCertification') {
      if (this._cachedCertifications) {
        condition.setCertificationValues(this._cachedCertifications);
      } else {
        this.personDetailsService
          .getCertifications()
          .pipe(first())
          .subscribe((certifications) => {
            this._cachedCertifications = certifications;
            condition.setCertificationValues(this._cachedCertifications);
          });
      }
    } else if (
      evidence.evidenceType == 'MemberCredential' ||
      evidence.evidenceType == 'LicenseeCredential'
    ) {
      condition.setValueType(SearchValueType.DateRange);
    }
    return condition;
  };

  private checkIfAvailableConditionsReady = () => {
    //keeping track of how many conditions are ready. Once all ready go ahead and combine
    if (this.existingConditions.length) {
      //existingConditions already set
      return;
    }
    if (this._conditionReadyCount < this.rawAvailableConditions.length) {
      return;
    }
    //all this.availableConditions are initialized
    if (!this.availableConditions) {
      //if able to build all conditions synchronously. Need to wait to call this.convertToSelectedCondition
      return;
    }
    //filter out conditions where valueType is not set (Complex fields)
    this.availableConditions = this.availableConditions.filter((c) => c.valueType);

    this.existingConditions = this.rawExistingConditions
      .map((condition) =>
        createSelectedCondition(condition, this.availableConditions, this.initSelectedCondition)
      )
      .sort((a, b) => a.filter?.displayName?.localeCompare(b.filter?.displayName));
  };

  /** Some EvidenceAutomationConditionItem need some async initialization. Called once each this.availableConditions[] is ready */
  private conditionReady = () => {
    this._conditionReadyCount++;

    this.checkIfAvailableConditionsReady();
  };

  private initSelectedCondition = (
    searchValue: string[],
    filterOperator: FilterOperator,
    filter: AutomationConditionItem
  ): {
    searchValues?: any[];
    searchValue?: string;
  } => {
    if (!searchValue?.length) {
      return {}; //no work to do; bail
    }
    if (
      filterOperator.actualOperator != FilterOperatorType.in &&
      filterOperator.actualOperator != FilterOperatorType.notIn
    ) {
      return { searchValue: searchValue[0] };
    }
    //in this case, condition.searchValue is a bunch of guids. Need to use filter.values to find corresponding objects for Guid
    const searchValues = searchValue
      .map((guid) => {
        const searchValue = filter?.values?.find((v) => v.searchValue == guid);
        if (!searchValue) {
          //should almost never happen, but I want to know if it does.
          //could happen if you have a condition using an option in the dropdown that was deleted.
          console.error(`could not find value for ${guid}. filter: ${JSON.stringify(filter)}`);
        }
        return searchValue;
      })
      .filter((obj) => !!obj); //only return mapped objects
    return { searchValues };
  };

  deleteFilter(index: number) {
    this.existingConditions.splice(index, 1);
    this.updateIsDisabled.emit(this.existingConditions.length === 0); //if there are zero selectedConditions; save button is disabled
  }

  deleteSubFilter(selectedCondition: SelectedCondition, index: number) {
    selectedCondition.selectedSubConditions.splice(index, 1);
  }
  addCondition() {
    this.existingConditions.push(new SelectedCondition(null));
    this.updateIsDisabled.emit(false);
  }

  addSubCondition(selectedCondition: SelectedCondition) {
    selectedCondition.selectedSubConditions.push(new SelectedCondition(null));
  }

  //** called when clicking save on parent component. Throws exception if invalid */
  apply = (): AutomationConditionModel[] => {
    this.existingConditions.forEach((item) => {
      item.isFilterApplied = true;
      item.selectedSubConditions.forEach((subItem) => {
        subItem.isFilterApplied = true;
      });
    });

    if (!this.isValid) throw Error('conditions not valid');
    return this.existingConditions.map((x) => x.buildConditionModel());
  };

  get showError(): boolean {
    if (this.isValid) {
      return false;
    }

    const isNotApplied = this.existingConditions.find((x) => !x.isFilterApplied);
    if (isNotApplied) {
      //1+ conditions haven't been "applied"
      return false;
    }
    return true;
  }

  private get isValid(): boolean {
    // All conditions must be valid, and if a condition has sub-filters, those must also be valid
    return this.existingConditions.every(
      (x) => x.isValid && (!x.hasSubFilter || x.isSubFilterValid)
    );
  }
}
